<template>
    <!-- AN-D5 — TWO `MetricStack` sections + a tapered divider between.
         Per AN-design-v2/complete/SPEC §3.4 the prior CSS-`order` reorder
         hack (DOM order ≠ visual order) is dropped here in favour of an
         honest DOM seam: the PRIMARY pair (DL+UL — the throughput
         hero numbers, lit + loud) sits above a brand `divider-h-tapered`
         hairline, then the SECONDARY pair (latency+jitter — the
         variability cluster, tight + quiet). DOM order IS visual order.
         The `dpi` variant only renders the primary section (no secondary,
         no divider — naturally; `secondary.length === 0`).

         The cascade-rung labels stay on each row so the row-stagger
         cascade still rides the W12-δ per-row spring decay; row indices
         span the WHOLE visible list (primary first, then secondary), so
         the "lead" / "mid" / "tail" ladder cuts cleanly across the seam.
    -->
    <div class="results-sections" :data-variant="variantStore.variant.id">
        <MetricStack
            v-if="primary.length > 0"
            :as="TransitionGroup"
            :rows="primary.length"
            :variant="variantStore.variant.id"
            register="result"
            name="result-row-enter"
            tag="div"
            class="results-section results-section--primary"
        >
            <MetricRow
                v-for="metric in primary"
                :key="metric.id"
                :icon="null"
                :data-metric="metric.id"
                :data-cascade-rung="cascadeRungFor(visibleIndex[metric.id])"
                :label="metric.label"
                :value="results[metric.id].formatted.value || '—'"
                :unit="results[metric.id].unit.value || metric.defaultUnit"
                :phase-color="metric.colorVarLabel"
                :digit-count="results[metric.id].formatted.value.length || 1"
                :color-tinted="rowTinted[metric.id]"
                :active="isRowActive(metric.id)"
                :style="{
                    '--metric-row-value-color': metric.colorVar,
                    '--metric-row-unit-color': 'var(--muted-foreground)',
                    '--phase-color-aura': metric.color,
                }"
            >
                <!-- AS-11 — the displayed digit is glass-ui's AnimatedDigit
                     (library-owned damping + font-feature register) wrapped
                     in the `.as11-value` fade-up host so the number reveals
                     via a brief fade-up entrance on the result-row enter.
                     `data-as11-revealed` gates the lift; `--as11-reveal-delay`
                     staggers the keyframe per row (both off useResultReveal,
                     the SAME clock as the row cascade). -->
                <template #value>
                    <span
                        class="as11-value"
                        :data-as11-revealed="
                            reveal.revealedFor(visibleIndex[metric.id])
                        "
                        :style="{
                            '--as11-reveal-delay': reveal.delayFor(
                                visibleIndex[metric.id],
                            ),
                        }"
                    >
                        <AnimatedDigit
                            :value="results[metric.id].raw.value"
                            :format="(v: number) => formatAmount(metric.id, v)"
                            :digit-count="
                                results[metric.id].formatted.value.length || 1
                            "
                            placeholder="—"
                        />
                    </span>
                </template>
                <template #aura>
                    <Pulse
                        variant="aura"
                        intensity="subtle"
                        class="result-value-aura"
                        :class="{ 'is-active': isRowActive(metric.id) }"
                        aria-hidden="true"
                    />
                </template>
            </MetricRow>
        </MetricStack>

        <!-- The hierarchy break. Brand divider-h-tapered — tracks the
             SECONDARY section width (narrower than the rows-stack so it
             reads as a section-internal seam, not a card-edge rail). Only
             paints when there IS a secondary section beneath it (i.e.,
             not the dpi variant). Marked aria-hidden — decorative. -->
        <hr
            v-if="primary.length > 0 && secondary.length > 0"
            class="section-divider divider-h-tapered"
            aria-hidden="true"
        />

        <MetricStack
            v-if="secondary.length > 0"
            :as="TransitionGroup"
            :rows="secondary.length"
            :variant="variantStore.variant.id"
            register="result"
            name="result-row-enter"
            tag="div"
            class="results-section results-section--secondary"
        >
            <MetricRow
                v-for="metric in secondary"
                :key="metric.id"
                :icon="null"
                :data-metric="metric.id"
                :data-cascade-rung="cascadeRungFor(visibleIndex[metric.id])"
                :label="metric.label"
                :value="results[metric.id].formatted.value || '—'"
                :unit="results[metric.id].unit.value || metric.defaultUnit"
                :phase-color="metric.colorVarLabel"
                :digit-count="results[metric.id].formatted.value.length || 1"
                :color-tinted="rowTinted[metric.id]"
                :active="isRowActive(metric.id)"
                :style="{
                    '--metric-row-value-color': metric.colorVar,
                    '--metric-row-unit-color': 'var(--muted-foreground)',
                    '--phase-color-aura': metric.color,
                }"
            >
                <!-- AS-11 — see the primary section: the secondary
                     variability pair reveals through the same AnimatedDigit
                     + `.as11-value` fade-up host. -->
                <template #value>
                    <span
                        class="as11-value"
                        :data-as11-revealed="
                            reveal.revealedFor(visibleIndex[metric.id])
                        "
                        :style="{
                            '--as11-reveal-delay': reveal.delayFor(
                                visibleIndex[metric.id],
                            ),
                        }"
                    >
                        <AnimatedDigit
                            :value="results[metric.id].raw.value"
                            :format="(v: number) => formatAmount(metric.id, v)"
                            :digit-count="
                                results[metric.id].formatted.value.length || 1
                            "
                            placeholder="—"
                        />
                    </span>
                </template>
                <template #aura>
                    <Pulse
                        variant="aura"
                        intensity="subtle"
                        class="result-value-aura"
                        :class="{ 'is-active': isRowActive(metric.id) }"
                        aria-hidden="true"
                    />
                </template>
            </MetricRow>
        </MetricStack>
    </div>
</template>

<script setup lang="ts">
import { TransitionGroup, computed, watch } from "vue";
import { useStagger } from "@mkbabb/glass-ui/motion-core";
import { MetricRow, MetricStack } from "@mkbabb/glass-ui/metric-stack";
import { AnimatedDigit } from "@mkbabb/glass-ui/animated-digit";
import { Pulse } from "@mkbabb/glass-ui/pulse";
import { MOTION_STAGGER_MS } from "@design";
import { useVariantStore } from "@src/stores/useVariantStore.ts";
import { useMetricRegistry } from "@features/speedtest/composables/useMetricRegistry.ts";
import { useMetricResults } from "@features/speedtest/composables/useMetricResult.ts";
import { useMetricFormatter } from "@features/speedtest/composables/useMetricFormatter.ts";
import { useMetricStateMachine } from "@features/speedtest/composables/useMetricStateMachine.ts";
import { useResultReveal } from "@features/speedtest/composables/useResultReveal.ts";
import type { MetricId } from "@src/types/speedtest.ts";

// Pinia-store-backed singletons — shared with the composition root.
const registry = useMetricRegistry();
const results = useMetricResults();
const formatter = useMetricFormatter();
const stateMachine = useMetricStateMachine();
const variantStore = useVariantStore();

// AS-11 — the completion VALUE is rendered through glass-ui's
// `AnimatedDigit` rather than the pre-formatted string MetricRow's
// `value` prop carried. AnimatedDigit owns the glass-ui damping pass
// (`useAnimatedNumber`) AND the tabular/ss01/lnum font-feature register,
// so the digit damping is the library's, never hand-rolled here. We feed
// it the RAW store value (not `results[id].smoothed`, which already rode
// `useAnimatedNumberMap`) so the number damps exactly ONCE on the way to
// the screen — AnimatedDigit is the single owner of the displayed digit's
// motion. The consumer-side fade-up entrance (the AS-11 reveal proper)
// rides the `result-row-enter` register via the `.as11-value` wrapper in
// `<style scoped>`; it is purely the ENTRANCE choreography, leaving the
// digit's value-damping to the primitive.
function formatAmount(id: MetricId, value: number): string {
    return formatter.format(id, value).amount || "—";
}

// AN-D5 / W22.1 — section partition. The throughput pair (DL+UL) is the
// PROTAGONIST tier; W22 splits it further — DOWNLOAD is the lone stage
// protagonist (the loudest number on the display ladder, ≤14rem at
// desktop), UPLOAD reads one rung down (≤8.5rem). The variability pair
// (latency+jitter) is the SECONDARY tier (tight + quiet, supporting
// telemetry). Both partitions reduce naturally under the `dpi` variant:
// only DL+UL are visible, so `primary` carries them and `secondary` is
// empty — no divider, no second section rendered.
const PRIMARY_IDS = new Set<MetricId>(["download", "upload"]);
const SECONDARY_IDS = new Set<MetricId>(["ping", "jitter"]);

const primary = computed(() =>
    registry.visible.value.filter((m) => PRIMARY_IDS.has(m.id)),
);
const secondary = computed(() =>
    registry.visible.value.filter((m) => SECONDARY_IDS.has(m.id)),
);

// Visible-list index map — the row-stagger cascade reads ONE index
// across the full visible list (so the lead/mid/tail rungs stay
// consistent with the W12-δ cadence regardless of which section a row
// lives in). The primary section renders first in the DOM, so its
// rows naturally claim the lead/mid rungs; secondary takes the tail.
const visibleIndex = computed<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    registry.visible.value.forEach((m, i) => {
        out[m.id] = i;
    });
    return out;
});

// Row-stagger climax: on `allComplete` rising edge, per-row colour-tint
// activation cascades at one `--motion-stagger-default` beat per row +
// one beat of lead-in via glass-ui's useStagger. CSS animates the value
// + unit colour from neutral → metric.color across 220ms.
const stagger = useStagger({
    items: registry.visible.value.length,
    delayMs: MOTION_STAGGER_MS.default,
    initialDelayMs: MOTION_STAGGER_MS.default,
    immediate: false,
});

// Map `revealed[i]` back to a `rowTinted[id]` shape so the template
// binding (`:color-tinted="rowTinted[metric.id]"`) stays terse.
const rowTinted = computed<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    registry.visible.value.forEach((m, i) => {
        out[m.id] = stagger.revealed.value[i] ?? false;
    });
    return out;
});

watch(
    stateMachine.allComplete,
    (now, prev) => {
        if (now && !prev) stagger.start();
        else if (!now && prev) stagger.reset();
    },
    { immediate: true },
);

// AS-11 — the value fade-up reveal. Rides the SAME `allComplete` rising
// edge + `--motion-stagger-default` cadence as the tint cascade above,
// so the number lift rhymes with the row arrival on one clock. The
// orchestrator owns ONLY the reveal gate + the per-row keyframe delay;
// the digit's value-damping is glass-ui's AnimatedDigit (in the `#value`
// slot) and the row slide is the TransitionGroup.
const reveal = useResultReveal({
    rowCount: () => registry.visible.value.length,
    active: () => stateMachine.allComplete.value,
});

// Active result-row pulse gate: a row is "active" while its metric is
// being sampled AND the overall run hasn't completed, so the
// post-complete cascade reveal doesn't keep pulsing.
function isRowActive(id: MetricId): boolean {
    if (stateMachine.allComplete.value) return false;
    return stateMachine.isActiveOrStarted(id).value;
}

// W12-δ — stagger cascade with per-row decay (Y6 §2.4 path).
function cascadeRungFor(index: number): "lead" | "mid" | "tail" {
    if (index === 0) return "lead";
    if (index < 3) return "mid";
    return "tail";
}
</script>

<style scoped>
/* ─────────────────────── AN-D5 — section model ─────────────────────────
   TWO `MetricStack` sections + a tapered divider between them. The
   visual hierarchy reads as:
     PRIMARY (loud, chroma-lit DL+UL throughput pair)
       — section-divider (tapered hairline) —
     SECONDARY (tight, quiet ping+jitter variability pair)
   The host `.results-sections` flex column owns the SECTION rhythm
   (the inter-section gap + the divider's margin); each MetricStack
   owns its own INTRA-section row-gap.
─────────────────────────────────────────────────────────────────────── */
.results-sections {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    /* Section-level rhythm — the seal↔primary breathing room. The host
       .complete-wrap already supplies the headline↔stack gap via the
       `--celebration-row-rhythm` token, so the value here only tunes
       between the PRIMARY and DIVIDER. The divider's own margin below
       supplies the rest. */
    gap: clamp(0.9rem, 2.4cqi, 1.4rem);
    /* The complete-wrap flex chain caps this stack at the chassis
       budget; participate honestly. */
    flex: 0 1 auto;
    min-block-size: 0;
}

/* The two sections own the row-gap rhythm: PRIMARY reads "members of
   one group"; the tight latency↔jitter pair (the explicit user ask)
   wins a far smaller gap so they read as ONE variability cluster.

   glass-ui's MetricStack ships a hardcoded `row-gap: clamp(0.25rem,
   1cqi, 0.75rem)` (no consumer token). Per [[feedback_glass_ui_first]]
   + [[feedback_library_gaps]] the IDIOMATIC long-term fix is a publisher
   `--metric-stack-row-gap` token; the AN prelude is already shipped
   though, so the consumer overrides `row-gap` directly via doubled-class
   specificity (`.metric-stack.results-section--*`, (0,2,0) outranks the
   publisher's (0,1,0)). No `:deep()`, no `!important`. Cleanest gestalt
   fix available without a second publisher round-trip. */
.metric-stack.results-section--primary {
    /* DL ↔ UL — members of one group, hero ratio. */
    row-gap: clamp(0.4rem, 1.4cqi, 1rem);
}
.metric-stack.results-section--secondary {
    /* LATENCY ↔ JITTER — TIGHT (~0.3rem floor, the explicit user ask).
       Reads as ONE variability cluster of two supporting telemetry
       lines, not two independent rows. */
    row-gap: clamp(0.12rem, 0.4cqi, 0.32rem);
}

/* ─────────────────────── Section divider (D5) ──────────────────────────
   Brand `divider-h-tapered` (glass-ui utilities.css:534-543) — a tapered
   gradient (transparent → ink → transparent). Tracks the SECONDARY
   section width (narrower than the rows-stack so the seam reads as
   instrument-internal, not card-edge). Centred. Margin claims the
   inter-section budget on both faces. */
.section-divider {
    width: clamp(12rem, 64cqi, 28rem);
    align-self: center;
    border: 0;
    margin-block: clamp(0.4rem, 1.2cqi, 0.85rem) clamp(0.6rem, 1.8cqi, 1.2rem);
    flex-shrink: 0;
}

/* ─────────────────── W22.1 — complete-hero protagonist ─────────────────
   DDR-W22-HERO (the FINAL word on AN-D5's reversal). The DOWNLOAD number
   is the stage PROTAGONIST on glass-ui's shipped display ladder; UPLOAD
   reads ONE rung down; the latency/jitter pair is the quiet variability
   cluster below. Heading-rung doctrine: the NUMBER shouts, the titular
   "DOWNLOAD"/"UPLOAD" words whisper.

   THE CLAMP LIFT (scope §3.1). The prior AN-D5 register capped the value
   at a 7.25rem REM-cap (~116px), so the cqi growth arm — which IS the
   viewport-relative lever (the `.metric-col` inline-size container) — was
   pinned far below a Fast.com-class scale on a wide card. W22 LIFTS that
   cap toward the glass-ui audacious peg (`--type-display-audacious` ≈ 352px,
   the fast.com peg) and steepens the cqi arm so the number renders
   genuinely viewport-relative at desktop:

     PROTAGONIST (download): floor 4.5rem, arm 26cqi, ceiling 14rem
       — at a ~1300px `.metric-col` (1440 desktop) the arm wins ~13.5rem
         (~216px); the rem-cap no longer pins it. The number IS the display.
     SECOND VOICE (upload): floor 3.25rem, arm 18cqi, ceiling 8.5rem
       — one rung down on the same ladder; co-present, clearly subordinate.

   The MetricRow font-size resolves the publisher formula
   `clamp(min, cqi · --result-row-scale · 3/max(3,digits), max)`, so the
   `3/max(3,digits)` term keeps a 4-digit value from overflowing while a
   3-digit gigabit reading lands at full protagonist scale. No new token,
   no `:deep()` — the publisher clamp knobs are the lever the spec names. */
.metric-stack[data-register="result"] .metric-row[data-metric="download"] {
    --metric-row-value-clamp-min: 4.5rem;
    --metric-row-value-clamp-cqi: 26cqi;
    --metric-row-value-clamp-max: 14rem;
    --metric-row-unit-clamp-min: 1.5rem;
    --metric-row-unit-clamp-max: 3rem;
    --metric-row-label-clamp-min: 0.9375rem;
    --metric-row-label-clamp-cqi: 2.75cqi;
    --metric-row-label-clamp-max: 1.25rem;
}
.metric-stack[data-register="result"] .metric-row[data-metric="upload"] {
    --metric-row-value-clamp-min: 3.25rem;
    --metric-row-value-clamp-cqi: 18cqi;
    --metric-row-value-clamp-max: 8.5rem;
    --metric-row-unit-clamp-min: 1.25rem;
    --metric-row-unit-clamp-max: 2.25rem;
    --metric-row-label-clamp-min: 0.875rem;
    --metric-row-label-clamp-cqi: 2.5cqi;
    --metric-row-label-clamp-max: 1.125rem;
}

/* SECONDARY tier — Ping + Jitter. A touch more presence than the prior
   ~28px treatment so the variability pair stays legible beneath the
   hero numbers (the SPEC §2 target is ~36px peak), but firmly
   subordinate. Tinted via the chassis cascade as before. */
.metric-stack[data-register="result"] .metric-row[data-metric="ping"],
.metric-stack[data-register="result"] .metric-row[data-metric="jitter"] {
    --metric-row-value-clamp-min: 1.5rem;
    --metric-row-value-clamp-cqi: 5.5cqi;
    --metric-row-value-clamp-max: 2.4rem;
    --metric-row-unit-clamp-min: 0.75rem;
    --metric-row-unit-clamp-max: 1.05rem;
    --metric-row-label-clamp-min: 0.6875rem;
    --metric-row-label-clamp-cqi: 1.75cqi;
    --metric-row-label-clamp-max: 0.875rem;
}

/* CLS pre-allocation per section. The PRIMARY section now reserves
   against the LIFTED hero ceilings — protagonist download (14rem) + the
   one-rung-down upload (8.5rem) ≈ 22.5rem at the ceiling, vs the floors
   (4.5rem + 3.25rem ≈ 7.75rem). Reserving the floor→ceiling band keeps
   the layout settled before the rows materialise so lifting the clamp
   does NOT regress the complete-stage CLS (the W17 dial-CLS class). The
   `--metric-stack-rows` factor falls away here — the two primary rows
   are asymmetric now, so the reservation sums their explicit ceilings
   rather than multiplying a single rung. The SECONDARY band is unchanged
   (the variability ceiling ~2.4rem × 2). */
.results-section--primary[data-register="result"],
.metric-stack.results-section--primary[data-register="result"] {
    min-block-size: clamp(7.75rem, 38cqi, 22.5rem);
}
.results-section--secondary[data-register="result"],
.metric-stack.results-section--secondary[data-register="result"] {
    min-block-size: clamp(
        calc(1.5rem * var(--metric-stack-rows, 2)),
        7cqi,
        calc(2.4rem * var(--metric-stack-rows, 2))
    );
}

/* The stacks participate in the bounded complete panel's flex chain
   (SpeedtestResults caps the chain at the chassis budget). */
.metric-stack {
    flex: 0 1 auto;
    min-block-size: 0;
}

/* Active result-row value pulse — unchanged from the pre-D5 treatment.
   The primitive's `.metric-row__value` host sits `position: relative`
   and the `aura` slot at `inset: 0`; this rule only tunes the opacity
   gate. The aura keeps the saturated `--phase-color` per row via
   `--phase-color-aura` — a luminous decoration, not body text. */
.result-value-aura {
    --pulse-aura-color: var(--phase-color-aura, currentColor);
    --pulse-aura-opacity-min: 0;
    --pulse-aura-opacity-max: 0.3;
    opacity: 0;
    transition: opacity var(--duration-normal, 0.3s) var(--ease-out, ease);
}
.result-value-aura.is-active {
    opacity: 1;
}

/* ─────────────────────── AS-11 — number fade-up ───────────────────────
   The completion VALUE digits reveal via a brief fade-up entrance: the
   `.as11-value` host (the MetricRow `#value` slot wrapper holding the
   glass-ui AnimatedDigit) starts a touch low + transparent, then lifts
   into place when `data-as11-revealed` flips true. The reveal gate +
   the per-row `--as11-reveal-delay` are both owned by `useResultReveal`,
   keyed off the SAME `allComplete` edge + `--motion-stagger-default`
   cadence as the row cascade — so the number lift rhymes with, and
   trails, the row arrival on one clock.

   The host transitions (not a keyframe) so the fade-up is a clean
   one-way settle with no replay churn; `--as11-reveal-delay` staggers
   the per-row lift. The digit's own value-damping is AnimatedDigit's —
   this host only owns the ENTRANCE. */
.as11-value {
    display: inline-flex;
    opacity: 0;
    transform: translateY(0.4rem);
    transition:
        opacity var(--motion-duration-staged) var(--ease-out),
        transform var(--motion-duration-staged) var(--motion-ease-overshoot);
    transition-delay: var(--as11-reveal-delay, 0ms);
}
.as11-value[data-as11-revealed="true"] {
    opacity: 1;
    transform: translateY(0);
}

/* WCAG 2.3.3 — under a reduced-motion preference the value fade-up
   collapses to an instant state: the number is simply present, no lift,
   no stagger. `useResultReveal`'s `useStagger` already flips every slot
   synchronously under PRM (respectReducedMotion default), so the gate is
   `true` immediately; this bracket drops the motion so the end-state
   paints at once. */
@media (prefers-reduced-motion: reduce) {
    .as11-value {
        opacity: 1;
        transform: none;
        transition: none;
    }
}

/* C3 / W12-δ — TransitionGroup row entry + per-row spring decay. The
   cascade rungs are assigned across the WHOLE visible list (primary
   first, then secondary), so the lead/mid/tail ladder sweeps cleanly
   across the section seam. */
.result-row-enter-active {
    transition:
        opacity var(--motion-duration-staged) ease-out,
        transform var(--motion-duration-staged) var(--motion-ease-overshoot);
}
.result-row-enter-from {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
}
.result-row-enter-to {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.result-row-enter-active[data-cascade-rung="lead"] {
    transition:
        opacity var(--motion-duration-staged) ease-out,
        transform var(--motion-duration-staged) var(--spring-bouncy);
}
.result-row-enter-active[data-cascade-rung="mid"] {
    transition:
        opacity var(--motion-duration-staged) ease-out,
        transform var(--motion-duration-staged) var(--spring-snappy);
}
.result-row-enter-active[data-cascade-rung="tail"] {
    transition:
        opacity var(--motion-duration-staged) ease-out,
        transform var(--motion-duration-staged) var(--spring-smooth);
}

/* WCAG 2.3.3 (Animation from Interactions) — under a reduced-motion
   preference the row-enter slide and the active-row aura cross-fade
   collapse to instant state changes. */
@media (prefers-reduced-motion: reduce) {
    .result-row-enter-active {
        transition: none;
    }
    .result-row-enter-from {
        transform: none;
    }
    .result-value-aura {
        transition: none;
    }
}
</style>
